import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import {
  useFetchModelsQuery,
  useDeployModelMutation,
  useDeleteModelDeploymentMutation,
  useGetModelQuery,
} from '../../../services/modelsApi'
import NoModelsCard from '../ModelsOverview/components/NoModelsCard'
import ErrorDialog from '../../modals/ErrorDialog'
import ConfirmDialog from '../../modals/ConfirmDialog'
import { Card, Row, Col, Badge, Button, Form, Spinner } from 'react-bootstrap'
import {
  Activity,
  Clock,
  Users,
  Zap,
  ExternalLink,
  Play,
  Square,
  Rocket,
  Trash2,
  Copy,
  FileText,
  X,
  ChevronRight,
} from 'lucide-react'
import { Modal } from 'react-bootstrap'
import { formatDuration, formatResponseTime } from '../../../utils/timeUtils'
import { toTitleCase } from '../../../utils'
import { getDeploymentStatusText } from '../../../utils/modelStatus'
import { Model } from '../../../types/models/model'

// Utility function to calculate model metrics
const calculateModelMetrics = (model: Model) => {
  const metrics = (model?.metrics || {}) as Record<string, unknown>

  const getNumber = (val: unknown): number => (typeof val === 'number' ? val : Number(val) || 0)

  const pick = (keys: string[]): number => {
    for (const key of keys) {
      if (metrics[key] != null) return getNumber(metrics[key])
    }
    return 0
  }

  // Try multiple aliases to be robust to backend key variants
  const precision = pick(['validation:precision', 'val:precision', 'precision'])
  const recall = pick(['validation:recall', 'val:recall', 'recall'])
  const mAP = pick([
    'validation:mAP50-95',
    'validation:map50-95',
    'validation:mAP',
    'val:mAP50-95',
    'mAP50-95',
    'mAP',
  ])
  const mAP50 = pick(['validation:mAP50', 'val:mAP50', 'mAP50'])
  const accuracy = pick(['validation:accuracy', 'val:accuracy', 'accuracy'])
  const trainAccuracy = pick(['train:accuracy', 'training:accuracy'])

  const f1 = precision && recall ? (2 * (precision * recall)) / (precision + recall) : 0

  const fmt = (n: number) => (n ? (n * 100).toFixed(1) + '%' : '—')

  // (debug logs removed)

  return {
    precision: fmt(precision),
    recall: fmt(recall),
    f1: f1 ? (f1 * 100).toFixed(1) + '%' : '—',
    mAP: fmt(mAP),
    mAP50: fmt(mAP50),
    accuracy: fmt(accuracy),
    trainAccuracy: fmt(trainAccuracy),
  }
}

const isObjectDetectionModel = (model?: Model): boolean => {
  if (!model) return false
  // Prefer explicit annotation_type when available
  if (model.annotation_type === 'bounding_box') return true
  if (model.annotation_type === 'classification') return false
  const metrics = (model.metrics || {}) as Record<string, unknown>
  // Infer from metrics keys
  const hasDetectionKeys = ['validation:mAP50', 'validation:mAP50-95', 'mAP50', 'mAP50-95'].some(
    (k) => metrics[k] != null
  )
  if (hasDetectionKeys) return true
  // Some payloads expose an ObjectiveMetricName like 'validation:mAP50'
  const objectiveName = (metrics['ObjectiveMetricName'] || '') as string
  if (typeof objectiveName === 'string' && objectiveName.toLowerCase().includes('map')) return true
  return false
}

// Map backend deployment statuses to user-friendly text (case-insensitive)
const formatDeploymentStatusText = (status?: string): string => {
  if (!status) return 'Ready'
  const normalized = status.toUpperCase().replace('-', '_') as any
  return getDeploymentStatusText(normalized)
}

// Returns true when a model is actively deploying/updating or still pending via optimistic state
const isDeployInFlight = (model: Model, optimistic: Record<string, Model>): boolean => {
  const s = (model.deployment?.status || '').toLowerCase()
  if (s === 'creating' || s === 'updating') return true
  // If we optimistically added it and the backend has not yet returned a status, show in-flight
  return !!optimistic[model.id] && !model.deployment?.status
}

const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: transparent;
`

const ContentSection = styled.div`
  flex: 1;
  min-height: 0;
  padding: 1.25rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const DeployedModelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const DeploymentCard = styled(Card)`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  transition: all 0.3s ease;
  height: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    border-color: rgba(102, 126, 234, 0.4);
  }

  .card-header {
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1rem 1.25rem;
  }

  .card-body {
    padding: 1.25rem;
  }

  .card-title {
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .card-subtitle {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .deployment-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.25);
    z-index: 10;
  }

  /* Dim content while overlay is visible */
  .deployment-overlay ~ .card-header,
  .deployment-overlay ~ .card-body,
  .deployment-overlay ~ div {
    opacity: 0.4;
    filter: blur(1px) grayscale(0.1);
    pointer-events: none;
  }
`

const StatusBadge = styled(Badge)`
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-weight: 500;
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  svg {
    width: 16px;
    height: 16px;
    color: #a0a0a0;
  }

  .stat-label {
    color: #a0a0a0;
    font-size: 0.85rem;
    min-width: 120px;
  }

  .stat-value {
    color: #fff;
    font-size: 0.9rem;
    font-weight: 500;
  }
`

const ActionButton = styled.button<{
  $variant?: 'primary' | 'danger' | 'success' | 'selected' | 'warning'
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  min-width: 120px;

  ${(props) => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #800085;
          color: white;
          &:hover:not(:disabled) {
            background: #6b006b;
          }
        `
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover:not(:disabled) {
            background: #dc2626;
          }
        `
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover:not(:disabled) {
            background: #059669;
          }
        `
      case 'warning':
        return `
          background: #f59e0b;
          color: white;
          &:hover:not(:disabled) {
            background: #d97706;
          }
        `
      case 'selected':
        return `
          background: #f59e0b;
          color: white;
          cursor: default;
        `
      default:
        return `
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.2);
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.15);
          }
        `
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #a0a0a0;

  h5 {
    color: #fff;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 0;
  }
`

const ApiDocsModal = styled(Modal)`
  .modal-content {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 16px;
    color: #fff;
  }

  .modal-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    background: transparent;
  }

  .modal-body {
    max-height: 70vh;
    overflow-y: auto;
    padding: 2rem;
  }

  .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: transparent;
  }
`

const CodeBlock = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  overflow-x: auto;
  position: relative;

  code {
    color: #a0ffa0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .copy-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
`

const DocsSection = styled.div`
  margin-bottom: 2rem;

  h4 {
    color: #fff;
    font-size: 1.1rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  h5 {
    color: #a0a0a0;
    font-size: 0.95rem;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }

  p {
    color: #e9ecef;
    margin-bottom: 1rem;
    line-height: 1.6;
  }

  ul {
    color: #e9ecef;
    margin-left: 1.5rem;
    margin-bottom: 1rem;

    li {
      margin-bottom: 0.5rem;
    }
  }
`

const EndpointInfo = styled.div`
  background: rgba(42, 42, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      color: #a0a0a0;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .value {
      color: #fff;
      font-family: monospace;
      font-size: 0.875rem;
      word-break: break-all;
      flex: 1;
      text-align: right;
      margin-left: 1rem;
    }
  }
`

interface ModelDeploymentViewProps {
  modelId?: string
  projectId?: string
  isDeployingModel?: boolean
  onDeployModel?: (modelId: string) => Promise<void>
}

const ModelDeploymentView: React.FC<ModelDeploymentViewProps> = ({
  modelId: propModelId,
  projectId: propProjectId,
  isDeployingModel: propIsDeployingModel,
  onDeployModel: propOnDeployModel,
}) => {
  const reduxSelectedProjectId = useAppSelector(selectSelectedProjectId)

  // Use props if provided, otherwise fall back to Redux state
  const selectedProjectId = propProjectId || reduxSelectedProjectId
  const [selectedModel, setSelectedModel] = useState<Model | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [showApiDocs, setShowApiDocs] = useState<Model | null>(null)
  const [showModelBrowser, setShowModelBrowser] = useState(false)
  const [selectedModelToDeploy, setSelectedModelToDeploy] = useState<Model | null>(null)
  const [isQuickDeploying, setIsQuickDeploying] = useState(false)
  // Optimistic deploying entries and short post-deploy polling
  const [optimisticDeploying, setOptimisticDeploying] = useState<Record<string, Model>>({})
  const [isPostDeployPolling, setIsPostDeployPolling] = useState(false)
  const [postDeployPollingUntil, setPostDeployPollingUntil] = useState<number | null>(null)
  const [optimisticMeta, setOptimisticMeta] = useState<Record<string, { stalled: boolean }>>({})

  // Fetch full model details when a model is selected to ensure metrics are available
  const { data: selectedModelData } = useGetModelQuery(selectedModelToDeploy?.id || '', {
    skip: !selectedModelToDeploy?.id,
  })

  // (debug logs removed)

  // Deployment mutations
  const [deployModel] = useDeployModelMutation()
  const [deleteModelDeployment] = useDeleteModelDeploymentMutation()

  // If we have a specific modelId, fetch that model's data
  const {
    data: specificModel,
    isLoading: isLoadingSpecificModel,
    refetch: refetchSpecificModel,
  } = useGetModelQuery(propModelId || '', {
    skip: !propModelId,
    pollingInterval: isPostDeployPolling ? 2500 : 0,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  // If no specific modelId, fetch all models
  const {
    data: modelsData,
    isLoading: isLoadingModels,
    refetch: refetchModels,
  } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    {
      skip: !selectedProjectId || !!propModelId,
      pollingInterval: isPostDeployPolling ? 2500 : 0,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  // Filter models that are deployed or in progress (IN_SERVICE or CREATING)
  const deployedModels = useMemo(() => {
    if (propModelId && specificModel) {
      // If we have a specific model, return it if it is IN_SERVICE or CREATING
      return specificModel.deployment?.status === 'IN_SERVICE' ||
        specificModel.deployment?.status === 'CREATING'
        ? [specificModel]
        : []
    }
    if (!modelsData?.models) return []
    // Show models with IN_SERVICE or CREATING deployment status
    const backend = modelsData.models.filter((model) => {
      const status = model.deployment?.status
      return status === 'IN_SERVICE' || status === 'CREATING'
    })
    // Merge optimistic entries not already present
    const optimisticList = Object.values(optimisticDeploying)
    const existingIds = new Set(backend.map((m) => m.id))
    const merged = [...backend]
    optimisticList.forEach((m) => {
      if (!existingIds.has(m.id)) merged.push(m)
    })
    return merged
  }, [modelsData?.models, optimisticDeploying, specificModel, propModelId])

  // Get models that are TRAINED but not deployed (for Quick Deploy)
  const deployableModels = useMemo(() => {
    if (!modelsData?.models || propModelId) return []
    return modelsData.models.filter(
      (model) =>
        model.state === 'TRAINED' &&
        (!model.deployment ||
          (model.deployment.status !== 'IN_SERVICE' && model.deployment.status !== 'CREATING'))
    )
  }, [modelsData?.models, propModelId])

  // Stop post-deploy polling after timeout or once no optimistic items remain
  React.useEffect(() => {
    if (isPostDeployPolling && postDeployPollingUntil && Date.now() > postDeployPollingUntil) {
      setIsPostDeployPolling(false)
      // Mark any remaining optimistic items as stalled instead of removing
      setOptimisticMeta((prev) => {
        const next = { ...prev }
        Object.keys(optimisticDeploying).forEach((id) => {
          next[id] = { stalled: true }
        })
        return next
      })
    }
    // If backend now shows all optimistic ids, clear them from optimistic lists and stop polling if none remain
    if (isPostDeployPolling && Object.keys(optimisticDeploying).length > 0 && modelsData?.models) {
      // Consider an optimistic id resolved as soon as the backend returns a model with that id
      const present = new Set(modelsData.models.map((m) => m.id))
      const remainingIds = Object.keys(optimisticDeploying).filter((id) => !present.has(id))
      if (remainingIds.length !== Object.keys(optimisticDeploying).length) {
        // Remove optimistic entries that now exist in backend
        setOptimisticDeploying((prev) => {
          const next = { ...prev }
          Object.keys(prev).forEach((id) => {
            if (present.has(id)) delete next[id]
          })
          return next
        })
        setOptimisticMeta((prev) => {
          const next = { ...prev }
          Object.keys(prev).forEach((id) => {
            if (present.has(id)) delete next[id]
          })
          return next
        })
      }
      if (remainingIds.length === 0) {
        setIsPostDeployPolling(false)
      }
    }
  }, [isPostDeployPolling, postDeployPollingUntil, optimisticDeploying, modelsData?.models])

  // Always reconcile optimistic entries against latest backend data
  React.useEffect(() => {
    if (!modelsData?.models) return
    const present = new Set(modelsData.models.map((m) => m.id))
    if (Object.keys(optimisticDeploying).length === 0) return
    setOptimisticDeploying((prev) => {
      const next = { ...prev }
      Object.keys(prev).forEach((id) => {
        if (present.has(id)) delete next[id]
      })
      return next
    })
    setOptimisticMeta((prev) => {
      const next = { ...prev }
      Object.keys(prev).forEach((id) => {
        if (present.has(id)) delete next[id]
      })
      return next
    })
  }, [modelsData?.models, optimisticDeploying])

  // Drive polling based on live statuses (both list and single-model views).
  // While any model is CREATING/UPDATING, we poll every 2.5s; we also extend the polling window during optimistic deploys.
  React.useEffect(() => {
    const anyInFlight = propModelId
      ? ['CREATING', 'UPDATING'].includes(specificModel?.deployment?.status || '')
      : (modelsData?.models || []).some((m) =>
          ['CREATING', 'UPDATING'].includes(m.deployment?.status || '')
        )

    if (anyInFlight) {
      if (!isPostDeployPolling) setIsPostDeployPolling(true)
      setPostDeployPollingUntil(Date.now() + 60_000)
    } else if (isPostDeployPolling && Object.keys(optimisticDeploying).length === 0) {
      setIsPostDeployPolling(false)
    }
  }, [
    propModelId,
    specificModel?.deployment?.status,
    modelsData?.models,
    isPostDeployPolling,
    optimisticDeploying,
  ])

  // Cleanup: stop polling and clear optimistic state on unmount
  React.useEffect(() => {
    return () => {
      setIsPostDeployPolling(false)
      setOptimisticDeploying({})
      setOptimisticMeta({})
    }
  }, [])

  const retryPollingFor = (id: string) => {
    setOptimisticMeta((prev) => ({ ...prev, [id]: { stalled: false } }))
    setIsPostDeployPolling(true)
    setPostDeployPollingUntil(Date.now() + 60_000)
  }

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'in_service':
        return 'success'
      case 'creating':
        return 'warning'
      case 'failed':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  // Helper functions to extract endpoint information from curl command
  const extractApiKey = (curlCommand: string) => {
    if (!curlCommand) return null
    const apiKeyMatch = curlCommand.match(
      /-H ['"]([^:]+):\s*([^'"]+)['"]|-H "([^:]+):\s*([^"]+)"|--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"/g
    )
    if (apiKeyMatch) {
      for (const match of apiKeyMatch) {
        const headerMatch = match.match(
          /-H ['"]([^:]+):\s*([^'"]+)['"]|-H "([^:]+):\s*([^"]+)"|--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"/
        )
        if (headerMatch) {
          const key = headerMatch[1] || headerMatch[3] || headerMatch[5] || headerMatch[7]
          const value = headerMatch[2] || headerMatch[4] || headerMatch[6] || headerMatch[8]
          if (
            key &&
            value &&
            (key.toLowerCase().includes('authorization') || key.toLowerCase().includes('api'))
          ) {
            return value
          }
        }
      }
    }
    return null
  }

  const extractEndpointUrl = (curlCommand: string) => {
    if (!curlCommand) return null
    const urlMatch = curlCommand.match(/POST ['"]([^'"]*)['"]/)
    let url = urlMatch ? urlMatch[1] : ''
    // Replace development domain with production domain
    url = url.replace('dev.emeraldai-dev.com', 'go-prod.agileview-ai.com')
    return url
  }

  const handleViewDetails = (model: Model) => {
    setSelectedModel(model)
  }

  const handleCloseDetails = () => {
    // Only allow closing if we're not viewing a specific model
    if (!propModelId) {
      setSelectedModel(null)
    }
  }

  const handleDeployModel = async () => {
    if (!propModelId) return

    try {
      setErrorMessage(null)
      // Optimistically add and start polling so the card shows immediately
      // Ensure the optimistic model has a deployment status so it shows up
      const baseModel =
        specificModel || ({ id: propModelId, name: 'Deploying…' } as unknown as Model)
      const optimisticModel: Model = {
        ...baseModel,
        deployment: baseModel.deployment || {
          model_name: baseModel.name || '',
          endpoint_name: '',
          endpoint_curl: '',
          status: 'CREATING' as const,
          deployed_at: new Date().toISOString(),
          expire_duration: 0,
          error: null,
          time_to_deploy: 0,
          total_invocations: 0,
          avg_invocation_rate: 0,
          last_invocation: '',
          class_statistics: null,
        },
      }
      setOptimisticDeploying((prev) => ({ ...prev, [propModelId]: optimisticModel }))
      setOptimisticMeta((prev) => ({ ...prev, [propModelId]: { stalled: false } }))
      setIsPostDeployPolling(true)
      setPostDeployPollingUntil(Date.now() + 60_000)
      if (propOnDeployModel) {
        await propOnDeployModel(propModelId)
      } else {
        await deployModel({ id: propModelId }).unwrap()
      }
      // Explicitly refetch to ensure updated deployment status is shown
      if (propModelId) {
        refetchSpecificModel()
      } else {
        refetchModels()
      }
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to deploy model')
      // Rollback optimistic state on failure
      setOptimisticDeploying((prev) => {
        const next = { ...prev }
        delete next[propModelId]
        return next
      })
      setOptimisticMeta((prev) => {
        const next = { ...prev }
        delete next[propModelId]
        return next
      })
    }
  }

  const handleDeleteDeployment = async (modelId?: string) => {
    const targetModelId = modelId || propModelId
    if (!targetModelId) return

    try {
      setIsDeleting(true)
      setErrorMessage(null)
      await deleteModelDeployment({ id: targetModelId }).unwrap()
      setShowDeleteConfirm(null)
      // If we deleted the selected model, clear selection
      if (selectedModel?.id === targetModelId) {
        setSelectedModel(null)
      }
      // Explicitly refetch to ensure updated deployment status is shown
      if (propModelId) {
        refetchSpecificModel()
      } else {
        refetchModels()
      }
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to undeploy model')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleQuickDeploy = async (model: Model) => {
    if (!model.id) return

    try {
      setIsQuickDeploying(true)
      setErrorMessage(null)
      // Close the modal first to avoid visible reflow flicker
      setShowModelBrowser(false)
      setSelectedModelToDeploy(null)
      // Optimistic add and start polling (after modal close to avoid flicker)
      // Create optimistic model with CREATING deployment status so it shows up in the list
      const optimisticModel: Model = {
        ...model,
        deployment: {
          model_name: model.name || '',
          endpoint_name: '',
          endpoint_curl: '',
          status: 'CREATING' as const,
          deployed_at: new Date().toISOString(),
          expire_duration: 0,
          error: null,
          time_to_deploy: 0,
          total_invocations: 0,
          avg_invocation_rate: 0,
          last_invocation: '',
          class_statistics: null,
        },
      }
      setOptimisticDeploying((prev) => ({ ...prev, [model.id]: optimisticModel }))
      setOptimisticMeta((prev) => ({ ...prev, [model.id]: { stalled: false } }))
      setIsPostDeployPolling(true)
      setPostDeployPollingUntil(Date.now() + 60_000)
      await deployModel({ id: model.id }).unwrap()
      // Wait a moment for backend to process, then refetch to ensure updated deployment status is shown
      await new Promise((resolve) => setTimeout(resolve, 500))
      await refetchModels()
    } catch (error: any) {
      setErrorMessage(error?.data?.message || 'Failed to deploy model')
      // Remove optimistic entry on failure
      setOptimisticDeploying((prev) => {
        const next = { ...prev }
        delete next[model.id]
        return next
      })
      setOptimisticMeta((prev) => {
        const next = { ...prev }
        delete next[model.id]
        return next
      })
    } finally {
      setIsQuickDeploying(false)
    }
  }

  const handleSelectModelToDeploy = (model: Model) => {
    setSelectedModelToDeploy(model)
  }

  if (propModelId && isLoadingSpecificModel) {
    return (
      <ViewContainer>
        <ContentSection>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading deployment information...</p>
          </div>
        </ContentSection>
      </ViewContainer>
    )
  }

  if (propModelId && specificModel && !specificModel.deployment) {
    return (
      <ViewContainer>
        <ContentSection>
          <div className="text-center py-5">
            <div className="mb-4">
              <Square size={64} className="text-muted" />
            </div>
            <h4 className="text-white mb-3">No Deployment Found</h4>
            <p className="text-muted mb-4">
              This model "{specificModel.name}" has not been deployed yet.
            </p>
            <Button variant="primary" onClick={() => (window.location.href = '/deployment')}>
              <Rocket size={16} className="me-2" />
              Go to Deployment Page
            </Button>
          </div>
        </ContentSection>
      </ViewContainer>
    )
  }

  // Only show NoModelsCard if data has loaded and there are no models
  if (!propModelId && !isLoadingModels && (!modelsData?.models || modelsData.models.length === 0)) {
    return (
      <ViewContainer>
        <ContentSection>
          <NoModelsCard onCreateModel={() => undefined} />
        </ContentSection>
      </ViewContainer>
    )
  }

  // Show loading state while models are loading
  if (!propModelId && isLoadingModels) {
    return (
      <ViewContainer>
        <ContentSection>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading deployments...</p>
          </div>
        </ContentSection>
      </ViewContainer>
    )
  }

  // If we're viewing a specific model, show the two-panel layout
  if (propModelId && specificModel) {
    const isDeployed = specificModel.deployment && specificModel.deployment.status === 'IN_SERVICE'
    const isDeploying =
      propIsDeployingModel ||
      (specificModel.deployment && specificModel.deployment.status === 'CREATING')
    const deploymentStatus = specificModel.deployment?.status?.toLowerCase()

    // Extract API key from curl command
    const extractApiKey = (curlCommand: string) => {
      const apiKeyMatch = curlCommand?.match(
        /-H ['"]([^:]+):\s*([^'"]+)['"]|-H "([^:]+):\s*([^"]+)"|--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"/g
      )
      if (apiKeyMatch) {
        for (const match of apiKeyMatch) {
          const headerMatch = match.match(
            /-H ['"]([^:]+):\s*([^'"]+)['"]|-H "([^:]+):\s*([^"]+)"|--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"/
          )
          if (headerMatch) {
            const key = headerMatch[1] || headerMatch[3] || headerMatch[5] || headerMatch[7]
            const value = headerMatch[2] || headerMatch[4] || headerMatch[6] || headerMatch[8]
            if (
              key &&
              value &&
              (key.toLowerCase().includes('authorization') || key.toLowerCase().includes('api'))
            ) {
              return value
            }
          }
        }
      }
      return null
    }

    // Extract endpoint URL from curl command
    const extractEndpointUrl = (curlCommand: string) => {
      const urlMatch = curlCommand?.match(/POST ['"]([^'"]*)['"]/)
      let url = urlMatch ? urlMatch[1] : ''
      // Replace development domain with production domain
      url = url.replace('dev.emeraldai-dev.com', 'go-prod.agileview-ai.com')
      return url
    }

    const apiKey = specificModel.deployment?.endpoint_curl
      ? extractApiKey(specificModel.deployment.endpoint_curl)
      : null
    const endpointUrl = specificModel.deployment?.endpoint_curl
      ? extractEndpointUrl(specificModel.deployment.endpoint_curl)
      : null

    return (
      <ViewContainer>
        <ContentSection>
          <h2
            style={{ color: '#fff', marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}
          >
            Deployment Management
          </h2>

          <Row className="g-4">
            {/* Left Panel - Deployment Actions */}
            <Col lg={4}>
              <Card
                className="h-100"
                style={{
                  background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                  border: '1px solid rgba(102, 126, 234, 0.2)',
                }}
              >
                <Card.Header
                  style={{
                    background: 'transparent',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Card.Title className="text-white mb-0">Deployment Actions</Card.Title>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                  <div className="mb-4">
                    <h6 className="text-white mb-3">Model Information</h6>
                    <div className="mb-2">
                      <strong className="text-white">Name:</strong>
                      <span className="text-light ms-2" style={{ color: '#e9ecef' }}>
                        {specificModel.name}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong className="text-white">Status:</strong>
                      <Badge bg={getStatusVariant(specificModel.state || '')} className="ms-2">
                        {toTitleCase(specificModel.state || 'Unknown')}
                      </Badge>
                    </div>
                    <div className="mb-3">
                      <strong className="text-white">Created:</strong>
                      <span className="text-light ms-2" style={{ color: '#e9ecef' }}>
                        {new Date(specificModel.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto">
                    {!isDeployed ? (
                      <div>
                        {isDeploying ? (
                          <div>
                            <p className="text-warning mb-3" style={{ color: '#ffc107' }}>
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Deployment in progress... This may take a few minutes.
                            </p>
                            <Button variant="secondary" disabled className="w-100">
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Deploying...
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-light mb-3" style={{ color: '#e9ecef' }}>
                              This model is ready for deployment. Deploy it to make it available via
                              API.
                            </p>
                            <Button
                              variant="primary"
                              onClick={handleDeployModel}
                              disabled={specificModel.state !== 'TRAINED'}
                              className="w-100"
                            >
                              <Rocket size={16} className="me-2" />
                              Deploy Model
                            </Button>
                            {specificModel.state !== 'TRAINED' && (
                              <small className="text-warning d-block mt-2">
                                Model must be trained before deployment
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-success mb-3">✓ Model is deployed and ready for use</p>
                        <Button
                          variant="success"
                          onClick={() => handleDeleteDeployment()}
                          disabled={isDeleting}
                          className="w-100"
                        >
                          {isDeleting ? (
                            <>
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Undeploying...
                            </>
                          ) : (
                            <>
                              <Rocket
                                size={16}
                                className="me-2"
                                style={{ transform: 'rotate(180deg)' }}
                              />
                              Undeploy
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Panel - Deployment Information */}
            <Col lg={8}>
              {isDeploying ? (
                <Card
                  className="h-100"
                  style={{
                    background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                  }}
                >
                  <Card.Header
                    style={{
                      background: 'transparent',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Card.Title className="text-white mb-0">Deployment in Progress</Card.Title>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      <div
                        className="spinner-border text-warning mb-3"
                        role="status"
                        style={{ width: '3rem', height: '3rem' }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="text-white mb-3">Deploying Model</h5>
                      <p className="text-warning mb-4" style={{ color: '#ffc107' }}>
                        Your model is being deployed. This process may take a few minutes.
                        <br />
                        Status: {specificModel.deployment?.status || 'CREATING'}
                      </p>
                      <div
                        className="p-3"
                        style={{
                          background: '#2a2a2a',
                          borderRadius: '6px',
                          border: '1px solid #404040',
                        }}
                      >
                        <small className="text-light" style={{ color: '#e9ecef' }}>
                          You can close this page and return later. The deployment will continue in
                          the background.
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ) : !isDeployed && !isDeploying ? (
                <Card
                  className="h-100"
                  style={{
                    background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                  }}
                >
                  <Card.Header
                    style={{
                      background: 'transparent',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Card.Title className="text-white mb-0">No Deployment Found</Card.Title>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      <Square size={64} className="text-light mb-3" style={{ color: '#e9ecef' }} />
                      <h5 className="text-white mb-3">No Deployment Found</h5>
                      <p className="text-light mb-4" style={{ color: '#e9ecef' }}>
                        Deploy this model to see deployment information and API endpoints.
                      </p>
                      <div
                        className="p-3"
                        style={{
                          background: '#2a2a2a',
                          borderRadius: '6px',
                          border: '1px solid #404040',
                        }}
                      >
                        <small className="text-light" style={{ color: '#e9ecef' }}>
                          Once deployed, you'll see API endpoints, usage statistics, and
                          configuration details here.
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ) : isDeployed ? (
                <Card
                  className="h-100"
                  style={{
                    background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                  }}
                >
                  <Card.Header
                    style={{
                      background: 'transparent',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Card.Title className="text-white mb-0">Deployment Information</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-4">
                      <h6 className="text-white mb-3">Deployment Status</h6>
                      <div className="d-flex align-items-center mb-3">
                        <Badge
                          bg={getStatusVariant(specificModel.deployment?.status || '')}
                          className="me-3"
                        >
                          {toTitleCase(
                            specificModel.deployment?.status?.replace('_', ' ') || 'Unknown'
                          )}
                        </Badge>
                        <span className="text-light" style={{ color: '#e9ecef' }}>
                          Deployed:{' '}
                          {specificModel.deployment?.deployed_at
                            ? new Date(specificModel.deployment.deployed_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true,
                                }
                              )
                            : 'Unknown'}
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h6 className="text-white mb-3">Usage Statistics</h6>
                      <Row className="g-3">
                        <Col md={6}>
                          <div
                            className="p-3"
                            style={{
                              background: '#2a2a2a',
                              borderRadius: '6px',
                              border: '1px solid #404040',
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <Zap className="text-primary me-2" size={20} />
                              <div>
                                <div className="text-white fw-bold">
                                  {specificModel.deployment?.total_invocations || 0}
                                </div>
                                <small className="text-light" style={{ color: '#e9ecef' }}>
                                  Total API Calls
                                </small>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div
                            className="p-3"
                            style={{
                              background: '#2a2a2a',
                              borderRadius: '6px',
                              border: '1px solid #404040',
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <Clock className="text-info me-2" size={20} />
                              <div>
                                <div className="text-white fw-bold">
                                  {specificModel.deployment?.avg_invocation_rate
                                    ? formatResponseTime(
                                        specificModel.deployment.avg_invocation_rate
                                      )
                                    : 'N/A'}
                                </div>
                                <small className="text-light" style={{ color: '#e9ecef' }}>
                                  Avg Response Time
                                </small>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="mb-4">
                      <h6 className="text-white mb-3">API Configuration</h6>
                      <Row className="g-3">
                        <Col md={8}>
                          <div className="mb-2">
                            <small className="text-light" style={{ color: '#e9ecef' }}>
                              Endpoint URL
                            </small>
                          </div>
                          <div
                            className="p-3"
                            style={{
                              background: '#2a2a2a',
                              borderRadius: '6px',
                              border: '1px solid #404040',
                            }}
                          >
                            <code
                              className="text-success"
                              style={{
                                color: '#28a745',
                                fontSize: '0.85rem',
                                wordBreak: 'break-all',
                              }}
                            >
                              {endpointUrl || 'Endpoint URL not available'}
                            </code>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-2">
                            <small className="text-light" style={{ color: '#e9ecef' }}>
                              API Key
                            </small>
                          </div>
                          <div
                            className="p-3"
                            style={{
                              background: '#2a2a2a',
                              borderRadius: '6px',
                              border: '1px solid #404040',
                            }}
                          >
                            <code
                              className="text-warning"
                              style={{
                                color: '#ffc107',
                                fontSize: '0.85rem',
                                wordBreak: 'break-all',
                              }}
                            >
                              {apiKey || 'API key not available'}
                            </code>
                          </div>
                        </Col>
                      </Row>
                      <div className="mt-2 d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            if (endpointUrl) {
                              navigator.clipboard.writeText(endpointUrl)
                            }
                          }}
                        >
                          Copy URL
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            if (apiKey) {
                              navigator.clipboard.writeText(apiKey)
                            }
                          }}
                        >
                          Copy API Key
                        </Button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h6 className="text-white mb-3">CURL Command</h6>
                      <div className="d-flex align-items-center">
                        <div
                          className="p-3 flex-grow-1"
                          style={{
                            background: '#2a2a2a',
                            borderRadius: '6px',
                            border: '1px solid #404040',
                          }}
                        >
                          <code
                            className="text-light"
                            style={{
                              fontSize: '0.75rem',
                              wordBreak: 'break-all',
                              color: '#e9ecef',
                            }}
                          >
                            {specificModel.deployment?.endpoint_curl ||
                              'CURL command not available'}
                          </code>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="ms-2"
                          onClick={() => {
                            if (specificModel.deployment?.endpoint_curl) {
                              navigator.clipboard.writeText(specificModel.deployment.endpoint_curl)
                            }
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <Button
                        variant="outline-primary"
                        onClick={() => handleViewDetails(specificModel)}
                        className="w-100"
                      >
                        <ExternalLink size={16} className="me-2" />
                        View Detailed API Documentation
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <Card
                  className="h-100"
                  style={{
                    background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                  }}
                >
                  <Card.Header
                    style={{
                      background: 'transparent',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Card.Title className="text-white mb-0">Deployment Information</Card.Title>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      <Square size={64} className="text-light mb-3" style={{ color: '#e9ecef' }} />
                      <h5 className="text-white mb-3">No Deployment Found</h5>
                      <p className="text-light mb-4" style={{ color: '#e9ecef' }}>
                        Deploy this model to see deployment information and API endpoints.
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          <ErrorDialog
            show={!!errorMessage}
            onHide={() => setErrorMessage(null)}
            message={errorMessage || ''}
          />
        </ContentSection>
      </ViewContainer>
    )
  }

  // Fallback to original layout for general deployment view
  return (
    <ViewContainer>
      <ContentSection>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
            Deployed Models ({deployedModels.length})
          </h2>
          {!propModelId && !isLoadingModels && deployableModels.length > 0 && (
            <ActionButton onClick={() => setShowModelBrowser(true)} $variant="primary">
              <Rocket size={16} />
              Deploy a Model
            </ActionButton>
          )}
        </div>

        {deployedModels.length === 0 ? (
          <EmptyState>
            <h5>No Deployed Models</h5>
            <p>Deploy your trained models to see them here</p>
          </EmptyState>
        ) : (
          <DeployedModelsGrid>
            {deployedModels.map((model) => (
              <DeploymentCard key={model.id}>
                {isDeployInFlight(model, optimisticDeploying) && (
                  <div className="deployment-overlay">
                    <Spinner animation="border" variant="light" />
                  </div>
                )}
                <Card.Header>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <Card.Title>{model.name}</Card.Title>
                      <Card.Subtitle>{model.description || 'No description'}</Card.Subtitle>
                    </div>
                    <StatusBadge bg={getStatusVariant(model.deployment?.status || '')}>
                      {model.deployment?.status ? (
                        formatDeploymentStatusText(model.deployment?.status)
                      ) : optimisticDeploying[model.id] ? (
                        optimisticMeta[model.id]?.stalled ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-light"
                            onClick={(e) => {
                              e.stopPropagation()
                              retryPollingFor(model.id)
                            }}
                            style={{ padding: '0.1rem 0.35rem' }}
                          >
                            Pending
                          </button>
                        ) : (
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        )
                      ) : (
                        'Unknown'
                      )}
                    </StatusBadge>
                  </div>
                </Card.Header>

                <Card.Body>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <StatItem>
                      <Users />
                      <span className="stat-label">Deployed At:</span>
                      <span className="stat-value">
                        {model.deployment?.deployed_at
                          ? new Date(model.deployment.deployed_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })
                          : 'Unknown'}
                      </span>
                    </StatItem>

                    <StatItem>
                      <Zap />
                      <span className="stat-label">API Calls:</span>
                      <span className="stat-value">{model.deployment?.total_invocations || 0}</span>
                    </StatItem>

                    <StatItem>
                      <Clock />
                      <span className="stat-label">Avg Response:</span>
                      <span className="stat-value">
                        {model.deployment?.avg_invocation_rate
                          ? formatResponseTime(model.deployment.avg_invocation_rate)
                          : 'N/A'}
                      </span>
                    </StatItem>

                    <StatItem>
                      <Activity />
                      <span className="stat-label">Uptime:</span>
                      <span className="stat-value">
                        {model.deployment?.deployed_at
                          ? formatDuration(
                              (Date.now() - new Date(model.deployment.deployed_at).getTime()) * 1000
                            )
                          : 'N/A'}
                      </span>
                    </StatItem>
                  </div>

                  {/* Endpoint Information */}
                  {model.deployment?.endpoint_curl && (
                    <div
                      style={{
                        marginBottom: '1.5rem',
                        padding: '0.75rem',
                        background: 'rgba(42, 42, 42, 0.5)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <small
                          className="text-light"
                          style={{ color: '#e9ecef', fontSize: '0.75rem', fontWeight: '600' }}
                        >
                          Endpoint URL
                        </small>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginTop: '0.25rem',
                          }}
                        >
                          <code
                            style={{
                              color: '#28a745',
                              fontSize: '0.75rem',
                              wordBreak: 'break-all',
                              flex: 1,
                            }}
                          >
                            {extractEndpointUrl(model.deployment.endpoint_curl) || 'N/A'}
                          </code>
                          <ActionButton
                            onClick={() => {
                              const url = extractEndpointUrl(model.deployment?.endpoint_curl || '')
                              if (url) navigator.clipboard.writeText(url)
                            }}
                            style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
                          >
                            <Copy size={14} />
                          </ActionButton>
                        </div>
                      </div>
                      <div>
                        <small
                          className="text-light"
                          style={{ color: '#e9ecef', fontSize: '0.75rem', fontWeight: '600' }}
                        >
                          API Key
                        </small>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginTop: '0.25rem',
                          }}
                        >
                          <code
                            style={{
                              color: '#ffc107',
                              fontSize: '0.75rem',
                              wordBreak: 'break-all',
                              flex: 1,
                            }}
                          >
                            {extractApiKey(model.deployment.endpoint_curl) || 'N/A'}
                          </code>
                          <ActionButton
                            onClick={() => {
                              const key = extractApiKey(model.deployment?.endpoint_curl || '')
                              if (key) navigator.clipboard.writeText(key)
                            }}
                            style={{ padding: '0.25rem 0.5rem', minWidth: 'auto' }}
                          >
                            <Copy size={14} />
                          </ActionButton>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
                    <ActionButton
                      onClick={() => setShowApiDocs(model)}
                      style={{ flex: '1', background: '#3b82f6', color: 'white', border: 'none' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#2563eb'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#3b82f6'
                      }}
                    >
                      <FileText size={16} />
                      API Docs
                    </ActionButton>
                    <ActionButton
                      $variant="success"
                      onClick={() => setShowDeleteConfirm(model.id)}
                      style={{ flex: '1' }}
                    >
                      <Rocket size={16} style={{ transform: 'rotate(180deg)' }} />
                      Undeploy
                    </ActionButton>
                  </div>
                </Card.Body>
              </DeploymentCard>
            ))}
          </DeployedModelsGrid>
        )}

        <ErrorDialog
          show={!!errorMessage}
          onHide={() => setErrorMessage(null)}
          message={errorMessage || ''}
        />

        <ConfirmDialog
          show={!!showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(null)}
          onConfirmRight={() => {
            if (showDeleteConfirm) {
              handleDeleteDeployment(showDeleteConfirm)
            }
          }}
          title="Undeploy Model"
          message={`Are you sure you want to undeploy "${
            deployedModels.find((m) => m.id === showDeleteConfirm)?.name
          }"? This action cannot be undone.`}
          confirmRightText={isDeleting ? 'Undeploying...' : 'Undeploy'}
          variant="danger"
          cancelText="Cancel"
        />

        {/* API Documentation Modal */}
        <ApiDocsModal show={!!showApiDocs} onHide={() => setShowApiDocs(null)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FileText
                size={20}
                className="me-2"
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              />
              API Documentation - {showApiDocs?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {showApiDocs ? (
              <>
                <DocsSection>
                  <h4>Endpoint Information</h4>
                  <EndpointInfo>
                    <div className="info-row">
                      <span className="label">Endpoint URL:</span>
                      <span className="value">
                        {showApiDocs.deployment?.endpoint_curl
                          ? extractEndpointUrl(showApiDocs.deployment.endpoint_curl) || 'N/A'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">API Key:</span>
                      <span className="value">
                        {showApiDocs.deployment?.endpoint_curl
                          ? extractApiKey(showApiDocs.deployment.endpoint_curl) || 'N/A'
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Method:</span>
                      <span className="value">POST</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Content-Type:</span>
                      <span className="value">application/json</span>
                    </div>
                  </EndpointInfo>
                </DocsSection>

                <DocsSection>
                  <h4>Overview</h4>
                  <p>
                    This API endpoint allows you to run inference on images using the deployed
                    model. Send a POST request with your image data, and the API will return
                    predictions.
                  </p>
                  <p>
                    The request body should contain the image data, and you must include your API
                    key in the Authorization header for authentication.
                  </p>
                </DocsSection>

                <DocsSection>
                  <h4>Request Format</h4>
                  <h5>Headers</h5>
                  <ul>
                    <li>
                      <code>Authorization</code>: Bearer token with your API key
                    </li>
                    <li>
                      <code>Content-Type</code>: application/json
                    </li>
                  </ul>
                  <h5>Request Body</h5>
                  <p>
                    The request body should be a JSON object containing the image data. The image
                    can be provided as:
                  </p>
                  <ul>
                    <li>Base64-encoded image string</li>
                    <li>Image URL (if supported by your deployment)</li>
                  </ul>
                </DocsSection>

                <DocsSection>
                  <h4>Code Examples</h4>

                  <h5>Python</h5>
                  <CodeBlock>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="copy-btn"
                      onClick={() => {
                        const code = `import requests
import base64
from pathlib import Path

# Endpoint URL
url = "${
                          showApiDocs?.deployment?.endpoint_curl
                            ? extractEndpointUrl(showApiDocs.deployment.endpoint_curl) || ''
                            : ''
                        }"

# Your API key
api_key = "${
                          showApiDocs?.deployment?.endpoint_curl
                            ? extractApiKey(showApiDocs.deployment.endpoint_curl) || ''
                            : ''
                        }"

# Read and encode image
image_path = "path/to/your/image.jpg"
with open(image_path, "rb") as image_file:
    image_data = base64.b64encode(image_file.read()).decode('utf-8')

# Prepare request
headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

payload = {
    "image": image_data
}

# Make request
response = requests.post(url, json=payload, headers=headers)

# Handle response
if response.status_code == 200:
    predictions = response.json()
    print("Predictions:", predictions)
else:
    print(f"Error: {response.status_code}")
    print(response.text)`
                        navigator.clipboard.writeText(code)
                      }}
                    >
                      <Copy size={12} className="me-1" />
                      Copy
                    </Button>
                    <code>{`import requests
import base64
from pathlib import Path

# Endpoint URL
url = "${
                      showApiDocs?.deployment?.endpoint_curl
                        ? extractEndpointUrl(showApiDocs.deployment.endpoint_curl) || ''
                        : ''
                    }"

# Your API key
api_key = "${
                      showApiDocs?.deployment?.endpoint_curl
                        ? extractApiKey(showApiDocs.deployment.endpoint_curl) || ''
                        : ''
                    }"

# Read and encode image
image_path = "path/to/your/image.jpg"
with open(image_path, "rb") as image_file:
    image_data = base64.b64encode(image_file.read()).decode('utf-8')

# Prepare request
headers = {
    "Authorization": f"Bearer {api_key}",
    "Content-Type": "application/json"
}

payload = {
    "image": image_data
}

# Make request
response = requests.post(url, json=payload, headers=headers)

# Handle response
if response.status_code == 200:
    predictions = response.json()
    print("Predictions:", predictions)
else:
    print(f"Error: {response.status_code}")
    print(response.text)`}</code>
                  </CodeBlock>

                  <h5>JavaScript (Fetch API)</h5>
                  <CodeBlock>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="copy-btn"
                      onClick={() => {
                        const code = `// Endpoint URL
const url = "${
                          showApiDocs?.deployment?.endpoint_curl
                            ? extractEndpointUrl(showApiDocs.deployment.endpoint_curl) || ''
                            : ''
                        }";

// Your API key
const apiKey = "${
                          showApiDocs?.deployment?.endpoint_curl
                            ? extractApiKey(showApiDocs.deployment.endpoint_curl) || ''
                            : ''
                        }";

// Convert image to base64 (browser example)
function imageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Run inference
async function runInference(imageFile) {
  try {
    const imageData = await imageToBase64(imageFile);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        image: imageData
      })
    });

    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const predictions = await response.json();
    console.log('Predictions:', predictions);
    return predictions;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}

// Usage
const fileInput = document.querySelector('input[type="file"]');
fileInput.addEventListener('change', async (e) => {
  const file = e.target.files[0];
  if (file) {
    const predictions = await runInference(file);
    console.log(predictions);
  }
});`
                        navigator.clipboard.writeText(code)
                      }}
                    >
                      <Copy size={12} className="me-1" />
                      Copy
                    </Button>
                    <code>{`// Endpoint URL
const url = "${
                      showApiDocs.deployment?.endpoint_curl
                        ? extractEndpointUrl(showApiDocs.deployment.endpoint_curl) || ''
                        : ''
                    }";

// Your API key
const apiKey = "${
                      showApiDocs.deployment?.endpoint_curl
                        ? extractApiKey(showApiDocs.deployment.endpoint_curl) || ''
                        : ''
                    }";

// Convert image to base64 (browser example)
function imageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Run inference
async function runInference(imageFile) {
  try {
    const imageData = await imageToBase64(imageFile);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer \${apiKey}\`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        image: imageData
      })
    });

    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }

    const predictions = await response.json();
    console.log('Predictions:', predictions);
    return predictions;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}

// Usage
const fileInput = document.querySelector('input[type="file"]');
fileInput.addEventListener('change', async (e) => {
  const file = e.target.files[0];
  if (file) {
    const predictions = await runInference(file);
    console.log(predictions);
  }
});`}</code>
                  </CodeBlock>

                  <h5>cURL</h5>
                  <CodeBlock>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="copy-btn"
                      onClick={() => {
                        if (showApiDocs?.deployment?.endpoint_curl) {
                          navigator.clipboard.writeText(showApiDocs.deployment.endpoint_curl)
                        }
                      }}
                    >
                      <Copy size={12} className="me-1" />
                      Copy
                    </Button>
                    <code>
                      {showApiDocs?.deployment?.endpoint_curl || 'curl command not available'}
                    </code>
                  </CodeBlock>
                </DocsSection>

                <DocsSection>
                  <h4>Response Format</h4>
                  <p>
                    On success, the API returns a JSON response with predictions. The exact format
                    depends on your model type, but typically includes:
                  </p>
                  <ul>
                    <li>
                      <code>predictions</code>: Array of detected objects or classifications
                    </li>
                    <li>
                      <code>confidence</code>: Confidence scores for each prediction
                    </li>
                    <li>
                      <code>bbox</code>: Bounding box coordinates (for object detection models)
                    </li>
                    <li>
                      <code>class</code>: Class label or category
                    </li>
                  </ul>
                  <h5>Example Response</h5>
                  <CodeBlock>
                    <code>{`{
  "predictions": [
    {
      "class": "car",
      "confidence": 0.95,
      "bbox": [100, 150, 200, 250]
    },
    {
      "class": "person",
      "confidence": 0.87,
      "bbox": [300, 400, 150, 350]
    }
  ]
}`}</code>
                  </CodeBlock>
                </DocsSection>

                <DocsSection>
                  <h4>Error Handling</h4>
                  <p>Common error responses:</p>
                  <ul>
                    <li>
                      <code>401 Unauthorized</code>: Invalid or missing API key
                    </li>
                    <li>
                      <code>400 Bad Request</code>: Invalid request format or missing required
                      fields
                    </li>
                    <li>
                      <code>500 Internal Server Error</code>: Server-side error during inference
                    </li>
                    <li>
                      <code>503 Service Unavailable</code>: Service temporarily unavailable
                    </li>
                  </ul>
                </DocsSection>
              </>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowApiDocs(null)}>
              Close
            </Button>
          </Modal.Footer>
        </ApiDocsModal>

        {/* Model Browser Modal */}
        <ApiDocsModal
          show={showModelBrowser}
          onHide={() => {
            setShowModelBrowser(false)
            setSelectedModelToDeploy(null)
          }}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Rocket
                size={20}
                className="me-2"
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              />
              Deploy a Model
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!selectedModelToDeploy ? (
              <div>
                <p style={{ color: '#e9ecef', marginBottom: '1.5rem' }}>
                  Select a trained model to deploy
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {deployableModels.map((model) => (
                    <Card
                      key={model.id}
                      style={{
                        background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.4)'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }}
                      onClick={() => handleSelectModelToDeploy(model)}
                    >
                      <Card.Body
                        style={{
                          padding: '1rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <Card.Title
                            style={{
                              color: '#fff',
                              fontSize: '1rem',
                              fontWeight: '600',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {model.name}
                          </Card.Title>
                          <Card.Subtitle
                            style={{
                              color: 'rgba(255, 255, 255, 0.6)',
                              fontSize: '0.85rem',
                              margin: 0,
                            }}
                          >
                            {model.description || 'No description'}
                          </Card.Subtitle>
                          <div
                            style={{
                              marginTop: '0.5rem',
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                            }}
                          >
                            <Badge
                              bg={getStatusVariant(model.state || '')}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {toTitleCase(model.state || 'Unknown')}
                            </Badge>
                            <span style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>
                              Created {new Date(model.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {/* Brief metric: F1 for OD; Accuracy for classification. If metrics unavailable, show dash. */}
                          <div
                            style={{
                              marginTop: '0.5rem',
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                            }}
                          >
                            {isObjectDetectionModel(model)
                              ? (() => {
                                  const hasPrecision =
                                    model.metrics &&
                                    (model.metrics as any)['validation:precision'] != null
                                  const hasRecall =
                                    model.metrics &&
                                    (model.metrics as any)['validation:recall'] != null
                                  if (!hasPrecision || !hasRecall) {
                                    return (
                                      <span
                                        style={{
                                          fontSize: '0.8rem',
                                          color: '#e9ecef',
                                          background: 'rgba(42,42,42,0.6)',
                                          border: '1px solid rgba(255,255,255,0.1)',
                                          borderRadius: '6px',
                                          padding: '2px 6px',
                                        }}
                                      >
                                        F1: —
                                      </span>
                                    )
                                  }
                                  const m = calculateModelMetrics(model)
                                  return (
                                    <span
                                      style={{
                                        fontSize: '0.8rem',
                                        color: '#e9ecef',
                                        background: 'rgba(42,42,42,0.6)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '6px',
                                        padding: '2px 6px',
                                      }}
                                    >
                                      F1: {m.f1}
                                    </span>
                                  )
                                })()
                              : (() => {
                                  const hasAcc =
                                    model.metrics &&
                                    (model.metrics as any)['validation:accuracy'] != null
                                  if (!hasAcc) {
                                    return (
                                      <span
                                        style={{
                                          fontSize: '0.8rem',
                                          color: '#e9ecef',
                                          background: 'rgba(42,42,42,0.6)',
                                          border: '1px solid rgba(255,255,255,0.1)',
                                          borderRadius: '6px',
                                          padding: '2px 6px',
                                        }}
                                      >
                                        Acc: —
                                      </span>
                                    )
                                  }
                                  const m = calculateModelMetrics(model)
                                  return (
                                    <span
                                      style={{
                                        fontSize: '0.8rem',
                                        color: '#e9ecef',
                                        background: 'rgba(42,42,42,0.6)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '6px',
                                        padding: '2px 6px',
                                      }}
                                    >
                                      Acc: {m.accuracy}
                                    </span>
                                  )
                                })()}
                          </div>
                        </div>
                        <ChevronRight
                          size={20}
                          style={{ color: 'rgba(255, 255, 255, 0.4)', flexShrink: 0 }}
                        />
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <Button
                  variant="link"
                  onClick={() => setSelectedModelToDeploy(null)}
                  style={{
                    color: '#667eea',
                    textDecoration: 'none',
                    padding: 0,
                    marginBottom: '1rem',
                  }}
                >
                  <X size={16} className="me-1" style={{ verticalAlign: 'middle' }} />
                  Back to models
                </Button>

                <DocsSection>
                  <h4>{selectedModelToDeploy.name}</h4>
                  <p style={{ color: '#e9ecef' }}>
                    {selectedModelToDeploy.description || 'No description'}
                  </p>

                  <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                      <div>
                        <span style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>Status:</span>
                        <Badge
                          bg={getStatusVariant(selectedModelToDeploy.state || '')}
                          className="ms-2"
                        >
                          {toTitleCase(selectedModelToDeploy.state || 'Unknown')}
                        </Badge>
                      </div>
                      <div>
                        <span style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>Created:</span>
                        <span style={{ color: '#fff', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                          {new Date(selectedModelToDeploy.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  {(selectedModelData?.state || selectedModelToDeploy.state || '').toLowerCase() ===
                    'trained' && (
                    <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                      <h5 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.1rem' }}>
                        Performance Metrics
                      </h5>
                      {(() => {
                        const modelForMetrics = selectedModelData || selectedModelToDeploy
                        const metrics = calculateModelMetrics(modelForMetrics as Model)
                        const isObjectDetection = isObjectDetectionModel(modelForMetrics as Model)

                        return (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: isObjectDetection
                                ? 'repeat(2, 1fr)'
                                : 'repeat(2, 1fr)',
                              gap: '1rem',
                            }}
                          >
                            {isObjectDetection ? (
                              <>
                                <div
                                  style={{
                                    background: 'rgba(42, 42, 42, 0.6)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                  <div
                                    style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}
                                  >
                                    {metrics.precision}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: 'rgba(42, 42, 42, 0.6)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    Recall
                                  </div>
                                  <div
                                    style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}
                                  >
                                    {metrics.recall}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: 'rgba(42, 42, 42, 0.6)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    F1 Score
                                  </div>
                                  <div
                                    style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}
                                  >
                                    {metrics.f1}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: 'rgba(42, 42, 42, 0.6)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    mAP
                                  </div>
                                  <div
                                    style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}
                                  >
                                    {metrics.mAP}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div
                                  style={{
                                    background: 'rgba(42, 42, 42, 0.6)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    Training Accuracy
                                  </div>
                                  <div
                                    style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}
                                  >
                                    {metrics.trainAccuracy}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    background: 'rgba(42, 42, 42, 0.6)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#a0a0a0',
                                      fontSize: '0.8rem',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    Validation Accuracy
                                  </div>
                                  <div
                                    style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600' }}
                                  >
                                    {metrics.accuracy}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  )}

                  <div style={{ marginTop: '2rem' }}>
                    <ActionButton
                      $variant="primary"
                      onClick={() => {
                        if (selectedModelToDeploy) {
                          handleQuickDeploy(selectedModelToDeploy)
                        }
                      }}
                      disabled={isQuickDeploying || !selectedModelToDeploy}
                      style={{ width: '100%', minWidth: 'auto' }}
                    >
                      {isQuickDeploying ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Deploying...
                        </>
                      ) : (
                        <>
                          <Rocket size={16} />
                          Deploy This Model
                        </>
                      )}
                    </ActionButton>
                  </div>
                </DocsSection>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModelBrowser(false)
                setSelectedModelToDeploy(null)
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </ApiDocsModal>
      </ContentSection>
    </ViewContainer>
  )
}

export default ModelDeploymentView
